import React, { useState, useEffect } from 'react';
import useSessionStorage from '../hooks/useSessionStorage';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  Line, Legend, PieChart, Pie, Cell, ComposedChart, Area, AreaChart
} from 'recharts';
import { PiggyBank, Percent, Settings, Calendar, Briefcase } from 'lucide-react';
import { setupExpensesService, inventoryService, expensesService, invoicesService } from '../services/supabaseService';
import { isPersonalCategory } from '../utils/expenseCategories';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parseDateSafe = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  const datePart = typeof dateStr === 'string' ? dateStr.split('T')[0].split(' ')[0] : '';
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
};

const InvestmentAnalytics = () => {
  const [selectedYear, setSelectedYear] = useSessionStorage('investment_selectedYear', new Date().getFullYear());
  const [initialSetupCost, setInitialSetupCost] = useSessionStorage('investment_initialSetupCost', 1500000);
  const [depreciationRate, setDepreciationRate] = useSessionStorage('investment_depreciationRate', 10);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [tempSetupCost, setTempSetupCost] = useState(initialSetupCost);
  
  const [inventory, setInventory] = useState([]);
  const [dbSetupCost, setDbSetupCost] = useState(0);

  const [stats, setStats] = useState({
    expenses: 0,
    revenue: 0,
    profit: 0,
    monthlyExpenses: MONTHS.map(m => ({ name: m, currentYear: 0 })),
    monthlyRevenue: MONTHS.map(m => ({ name: m, currentYear: 0 }))
  });
  const [loading, setLoading] = useState(true);

  // Fetch setup expenses sum from database
  useEffect(() => {
    const fetchSetupExpenses = async () => {
      try {
        const data = await setupExpensesService.getAll();
        if (Array.isArray(data) && data.length > 0) {
          const sum = data.reduce((acc, item) => acc + Number(item.amount || 0), 0);
          setDbSetupCost(sum);
        } else {
          setDbSetupCost(0);
        }
      } catch (err) {
        console.error('Error fetching setup expenses for investment analysis:', err);
      }
    };
    fetchSetupExpenses();

    window.addEventListener('setup-expenses-updated', fetchSetupExpenses);
    return () => {
      window.removeEventListener('setup-expenses-updated', fetchSetupExpenses);
    };
  }, []);

  // Fetch inventory data
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const data = await inventoryService.getAll();
        setInventory(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching inventory for investment analysis:', err);
      }
    };
    fetchInventory();
  }, []);

  // Fetch financial stats
  useEffect(() => {
    const fetchFinancialStats = async () => {
      try {
        const [allExpenses, allInvoices] = await Promise.all([
          expensesService.getAll(),
          invoicesService.getAll()
        ]);

        const yearNum = parseInt(selectedYear, 10);
        const monthlyExp = new Array(12).fill(0);
        const monthlyRev = new Array(12).fill(0);

        (allExpenses || []).forEach(e => {
          if (!e.expense_date || isPersonalCategory(e.category)) return;
          const d = parseDateSafe(e.expense_date);
          if (d && d.getFullYear() === yearNum) {
            monthlyExp[d.getMonth()] += Number(e.amount || 0);
          }
        });

        (allInvoices || []).forEach(inv => {
          if (!inv.payment_date || inv.status === 'Unpaid') return;
          const d = parseDateSafe(inv.payment_date);
          if (d && d.getFullYear() === yearNum) {
            const rent = Number(inv.monthly_rent || 0);
            monthlyRev[d.getMonth()] += rent;
          }
        });

        const totalExpenses = monthlyExp.reduce((a, b) => a + b, 0);
        const totalRevenue = monthlyRev.reduce((a, b) => a + b, 0);

        setStats({
          expenses: totalExpenses,
          revenue: totalRevenue,
          profit: totalRevenue - totalExpenses,
          monthlyExpenses: MONTHS.map((m, i) => ({ name: m, currentYear: monthlyExp[i] })),
          monthlyRevenue: MONTHS.map((m, i) => ({ name: m, currentYear: monthlyRev[i] }))
        });
      } catch (err) {
        console.error('Error computing financial stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFinancialStats();
  }, [selectedYear]);

  // Calculate live asset inventory cost
  const totalInventoryAssetValue = inventory.reduce((sum, item) => {
    return sum + ((Number(item.quantity) || 0) * (Number(item.cost) || 0));
  }, 0);

  // Total Initial Setup Cost is either fetched from DB (if present) or fallback to configured session storage value
  const activeSetupCost = dbSetupCost > 0 ? dbSetupCost : initialSetupCost;

  // Total Capital Expenditure (CapEx) = Setup Cost + Equipment/Inventory Value
  const totalCapEx = Number(activeSetupCost) + totalInventoryAssetValue;

  // Operating Expenses (OpEx) for the year
  const totalOpEx = stats.expenses;

  // Revenue & Profits
  const netProfit = stats.profit;

  // Return on Investment (ROI)
  const annualROI = totalCapEx > 0 ? ((netProfit / totalCapEx) * 100) : 0;

  // Payback period (in years / months)
  const monthlyAverageProfit = netProfit / 12;
  const paybackMonths = monthlyAverageProfit > 0 ? (totalCapEx / monthlyAverageProfit) : null;

  // 1. Data for CapEx vs OpEx Chart
  const capExVsOpExData = [
    { name: 'Initial Setup', value: Number(activeSetupCost) },
    { name: 'Equipment & Inventory', value: totalInventoryAssetValue },
    { name: 'Operating Expenses', value: totalOpEx }
  ];

  // 2. Data for Break-even & Payback Projection (Cumulative over 12 months)
  let cumulativeProfit = -totalCapEx;
  const breakEvenData = [{
    name: 'Start',
    'Net Balance': cumulativeProfit,
    'Cumulative Revenue': 0,
    'Cumulative Expenses': totalCapEx
  }];

  let runningRevenue = 0;
  let runningExpenses = totalCapEx;

  stats.monthlyRevenue.forEach((revItem, index) => {
    const expItem = stats.monthlyExpenses[index] || { currentYear: 0 };
    runningRevenue += revItem.currentYear || 0;
    runningExpenses += expItem.currentYear || 0;
    cumulativeProfit = runningRevenue - runningExpenses;
    
    breakEvenData.push({
      name: revItem.name,
      'Net Balance': cumulativeProfit,
      'Cumulative Revenue': runningRevenue,
      'Cumulative Expenses': runningExpenses
    });
  });

  // 3. Asset Depreciation Projection (5 Years)
  const depreciationProj = [];
  let remainingAssetValue = totalInventoryAssetValue;
  for (let year = 0; year <= 5; year++) {
    const depExpense = year === 0 ? 0 : remainingAssetValue * (depreciationRate / 100);
    remainingAssetValue = year === 0 ? totalInventoryAssetValue : remainingAssetValue - depExpense;
    depreciationProj.push({
      year: `Year ${year}`,
      'Asset Value': Math.round(remainingAssetValue),
      'Cumulative Depreciation': Math.round(totalInventoryAssetValue - remainingAssetValue)
    });
  }

  const handleSaveSettings = (e) => {
    e.preventDefault();
    setInitialSetupCost(tempSetupCost);
    setIsSettingsOpen(false);
  };

  if (loading) {
    return <div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>Loading analytics...</div>;
  }

  return (
    <div>
      {/* Header section */}
      <div className="flex justify-between items-center mb-5 flex-wrap gap-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Investment & ROI Analytics</h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>
            Analyze capital returns, asset valuation, depreciation, and operational break-even trends.
          </p>
        </div>
        <div className="flex items-center flex-wrap" style={{ gap: '1rem' }}>
          <button 
            className="flex items-center gap-2"
            onClick={() => { setTempSetupCost(activeSetupCost); setIsSettingsOpen(true); }}
            style={{ 
              padding: '0.4rem 0.75rem', 
              borderRadius: '0.5rem', 
              border: '1px solid var(--border-color)', 
              backgroundColor: 'rgba(255,255,255,0.05)', 
              color: 'var(--text-primary)',
              fontSize: '0.875rem'
            }}
          >
            <Settings size={14} /> CapEx Settings
          </button>
          <select 
            value={selectedYear} 
            onChange={e => setSelectedYear(parseInt(e.target.value))}
            style={{ padding: '0.4rem 0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)', fontSize: '0.875rem' }}
          >
            {[...Array(5)].map((_, i) => {
              const year = new Date().getFullYear() - i;
              return <option key={year} value={year} style={{ color: '#000' }}>{year}</option>;
            })}
          </select>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        
        {/* Total CapEx */}
        <div className="card flex items-center gap-6" style={{ padding: '1.25rem', borderTop: '3px solid #8b5cf6' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Briefcase size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Total Invested (CapEx)</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              ₹{totalCapEx.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Live Inventory Assets */}
        <div className="card flex items-center gap-6" style={{ padding: '1.25rem', borderTop: '3px solid #3b82f6' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PiggyBank size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Live Asset Inventory</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              ₹{totalInventoryAssetValue.toLocaleString()}
            </div>
          </div>
        </div>

        {/* ROI % */}
        <div className="card flex items-center gap-6" style={{ padding: '1.25rem', borderTop: `3px solid ${annualROI >= 0 ? '#10b981' : '#ef4444'}` }}>
          <div style={{ 
            padding: '0.75rem', 
            backgroundColor: annualROI >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
            color: annualROI >= 0 ? 'var(--accent-color)' : 'var(--danger-color)', 
            borderRadius: '0.5rem', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <Percent size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Annual ROI ({selectedYear})</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: annualROI >= 0 ? 'var(--accent-color)' : 'var(--danger-color)' }}>
              {annualROI.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Payback Period */}
        <div className="card flex items-center gap-6" style={{ padding: '1.25rem', borderTop: '3px solid #f59e0b' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Calendar size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Est. Payback Period</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {paybackMonths ? `${(paybackMonths / 12).toFixed(1)} Years (${Math.round(paybackMonths)} mos)` : 'N/A (Loss)'}
            </div>
          </div>
        </div>

      </div>

      {/* Main Charts Section */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        
        {/* Payback & Break-even Tracker */}
        <div className="card" style={{ padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '1.25rem', marginTop: 0, color: 'var(--text-primary)', fontWeight: '600' }}>Cumulative Break-even & Payback (Year {selectedYear})</h3>
          <div style={{ height: '300px', width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={breakEvenData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                <XAxis dataKey="name" stroke="var(--text-secondary)" tick={{ fontSize: 11 }} />
                <YAxis stroke="var(--text-secondary)" tick={{ fontSize: 11 }} formatter={v => `₹${v >= 100000 ? `${v / 1000}k` : v}`} />
                <RechartsTooltip 
                  contentStyle={{ backgroundColor: 'var(--surface-color)', borderColor: 'var(--border-color)', borderRadius: '0.5rem', color: '#fff', fontSize: '0.85rem' }}
                  itemStyle={{ color: '#fff' }}
                  formatter={(value) => [`₹${value.toLocaleString()}`]}
                />
                <Legend wrapperStyle={{ fontSize: '0.85rem', paddingTop: '10px' }} />
                <Area type="monotone" dataKey="Net Balance" fill="rgba(16, 185, 129, 0.1)" stroke="var(--accent-color)" strokeWidth={2} />
                <Line type="monotone" dataKey="Cumulative Revenue" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="Cumulative Expenses" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Investment breakdown & operating expenses */}
        <div className="card flex flex-col justify-between" style={{ padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '1.25rem', marginTop: 0, color: 'var(--text-primary)', fontWeight: '600' }}>CapEx vs OpEx Structure</h3>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '220px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={capExVsOpExData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {capExVsOpExData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  contentStyle={{ backgroundColor: 'var(--surface-color)', borderColor: 'var(--border-color)', borderRadius: '0.5rem', color: '#fff', fontSize: '0.85rem' }}
                  formatter={(value) => [`₹${value.toLocaleString()}`]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0 1rem' }}>
            {capExVsOpExData.map((item, index) => (
              <div key={item.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: COLORS[index % COLORS.length] }}></div>
                  <span style={{ color: 'var(--text-secondary)' }}>{item.name}</span>
                </div>
                <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>₹{item.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Depreciation Widget Section */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        
        {/* Controls & Projection Summary */}
        <div className="card col-span-1 flex flex-col justify-between" style={{ padding: '1.5rem' }}>
          <div>
            <h3 style={{ fontSize: '1rem', marginBottom: '1rem', marginTop: 0, color: 'var(--text-primary)', fontWeight: '600' }}>Asset Depreciation</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: '1.5', marginBottom: '1.5rem' }}>
              Project the value decay of capital assets (furniture, appliances) over 5 years.
            </p>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <div className="flex justify-between mb-2">
                <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Annual Decay Rate</span>
                <span style={{ fontSize: '0.875rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>{depreciationRate}%</span>
              </div>
              <input 
                type="range" 
                min="0" 
                max="30" 
                step="1"
                value={depreciationRate} 
                onChange={e => setDepreciationRate(parseInt(e.target.value))}
                style={{ width: '100%', cursor: 'pointer', accentColor: 'var(--primary-color)' }}
              />
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Initial Book Value:</span>
              <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>₹{totalInventoryAssetValue.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Estimated Value (Year 5):</span>
              <span style={{ fontWeight: '600', color: 'var(--accent-color)' }}>
                ₹{depreciationProj[5]?.['Asset Value']?.toLocaleString() || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Projection Chart */}
        <div className="card col-span-2" style={{ padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '1.25rem', marginTop: 0, color: 'var(--text-primary)', fontWeight: '600' }}>5-Year Amortization Decay Curve</h3>
          <div style={{ height: '220px', width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={depreciationProj} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorAsset" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--text-secondary)" tick={{ fontSize: 11 }} />
                <YAxis stroke="var(--text-secondary)" tick={{ fontSize: 11 }} formatter={v => `₹${v >= 100000 ? `${v / 1000}k` : v}`} />
                <RechartsTooltip 
                  contentStyle={{ backgroundColor: 'var(--surface-color)', borderColor: 'var(--border-color)', borderRadius: '0.5rem', color: '#fff', fontSize: '0.85rem' }}
                  formatter={(value) => [`₹${value.toLocaleString()}`]}
                />
                <Area type="monotone" dataKey="Asset Value" stroke="var(--accent-color)" fillOpacity={1} fill="url(#colorAsset)" strokeWidth={2} />
                <Area type="monotone" dataKey="Cumulative Depreciation" stroke="#ef4444" fill="none" strokeWidth={1.5} strokeDasharray="3 3" />
                <Legend wrapperStyle={{ fontSize: '0.85rem', paddingTop: '10px' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      {/* CapEx Settings Modal */}
      {isSettingsOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '100%', maxWidth: '450px', padding: '2rem', zIndex: 1001 }}>
            <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', marginTop: 0 }}>Configure Capital Expenditures</h3>
            <form onSubmit={handleSaveSettings}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Initial Property Setup / Lease Cost (₹)
                </label>
                {dbSetupCost > 0 && (
                  <div style={{ marginBottom: '1rem', padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--primary-color)', lineHeight: '1.4' }}>
                      <strong>Active Sync:</strong> Currently using the sum of expenses from the <strong>Initial Setup Expenses Page</strong> (₹{dbSetupCost.toLocaleString()}). To change this amount, add or edit items on that page.
                    </p>
                  </div>
                )}
                <input 
                  required 
                  type="number" 
                  disabled={dbSetupCost > 0}
                  value={tempSetupCost} 
                  onChange={e => setTempSetupCost(Number(e.target.value))}
                  placeholder="e.g. 1500000"
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    borderRadius: '0.5rem', 
                    border: '1px solid var(--border-color)',
                    opacity: dbSetupCost > 0 ? 0.5 : 1,
                    cursor: dbSetupCost > 0 ? 'not-allowed' : 'auto'
                  }}
                />
                <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  This setup cost represents building rent advance, interior decor, or security deposits paid to land owners.
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button 
                  type="button" 
                  onClick={() => setIsSettingsOpen(false)}
                  style={{ 
                    padding: '0.6rem 1.25rem', 
                    borderRadius: '0.5rem', 
                    border: '1px solid var(--border-color)', 
                    background: 'rgba(255,255,255,0.05)', 
                    color: 'var(--text-primary)' 
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={dbSetupCost > 0}
                  className="btn-primary"
                  style={{ 
                    padding: '0.6rem 1.25rem', 
                    borderRadius: '0.5rem',
                    opacity: dbSetupCost > 0 ? 0.5 : 1,
                    cursor: dbSetupCost > 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Save Config
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestmentAnalytics;
